import { useEffect } from 'react';
import { Box, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import TelInput from 'components/Common/TelInput';
import { useUpdateTelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/hook';
import { TelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/types';
import { defaultCountryFromNumber } from '../numberDetailFields';

type Props = {
  open: boolean;
  endpoint: TelephonyEndpoint | null;
  onClose: () => void;
};

type FormValues = {
  phone_number: string;
  friendly_name: string;
  agent_id: string;
  origination_uri: string;
  allowed_addresses: string;
};

const helperSx = { fontSize: 12, color: '#6B7280', mt: 0.75, mb: 2.25, lineHeight: 1.45 };
const shrinkLabel = { shrink: true };

function configString(config: Record<string, unknown> | null | undefined, key: string) {
  const value = config?.[key];
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '';
}

function allowedAddressesValue(config: Record<string, unknown> | null | undefined) {
  const addresses = config?.allowed_addresses;
  if (!Array.isArray(addresses)) return '';
  return addresses
    .map((item) => String(item))
    .filter(Boolean)
    .join(', ');
}

export default function EditFriendlyNameDrawer({ open, endpoint, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { mutateAsync: updateEndpoint, isPending } = useUpdateTelephonyEndpoint();
  const isAsterisk = endpoint?.provider === 'asterisk';
  const { control, reset, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      phone_number: '',
      friendly_name: '',
      agent_id: '',
      origination_uri: '',
      allowed_addresses: ''
    }
  });

  useEffect(() => {
    if (!open || !endpoint) return;
    const config = endpoint.connection_config || {};
    reset({
      phone_number: endpoint.phone_number || '',
      friendly_name: endpoint.friendly_name || '',
      agent_id: configString(config, 'agent_token') || endpoint.phone_number || '',
      origination_uri: configString(config, 'address'),
      allowed_addresses: allowedAddressesValue(config)
    });
  }, [open, endpoint, reset]);

  const onSave = handleSubmit(async (values) => {
    if (!endpoint) return;
    await updateEndpoint({
      id: endpoint.id,
      payload: {
        telephony_endpoint: {
          friendly_name: values.friendly_name.trim()
        }
      }
    });
    onClose();
  });

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 880,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom='1px solid #E5E7EB'
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          Edit
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={2.5}>
        {!isAsterisk && (
          <>
            <TelInput
              name='phone_number'
              control={control}
              label='Phone Number'
              fullWidth
              disabled
              defaultCountry={defaultCountryFromNumber(endpoint)}
              preferredCountries={['US', 'CA', 'IE', 'GB']}
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>This number cannot be changed.</Typography>
          </>
        )}

        <Input
          name='friendly_name'
          control={control}
          label='Friendly Name'
          fullWidth
          placeholder='e.g. Linha Commercial'
          InputLabelProps={shrinkLabel}
        />
        <Typography sx={helperSx}>Shown instead of the number in your lists. You can change it anytime.</Typography>

        {isAsterisk && (
          <>
            <Input
              name='agent_id'
              control={control}
              label='Agent ID'
              fullWidth
              disabled
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>This value cannot be changed.</Typography>

            <Input
              name='origination_uri'
              control={control}
              label='Origination URI'
              fullWidth
              disabled
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>This value cannot be changed.</Typography>

            <Input
              name='allowed_addresses'
              control={control}
              label='Allowed Addresses'
              fullWidth
              disabled
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={{ ...helperSx, mb: 0 }}>This value cannot be changed.</Typography>
          </>
        )}
      </Box>

      <Box
        display='flex'
        justifyContent='flex-end'
        gap={1.5}
        px={3}
        py={2.5}
        borderTop='1px solid #E5E7EB'
        bgcolor='#fff'
      >
        <Button
          variant='outlined'
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderColor: '#E5E7EB',
            color: '#111827',
            px: 2.5,
            bgcolor: '#fff',
            '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' }
          }}
        >
          Cancel
        </Button>
        <Button variant='contained' color='primary' onClick={onSave} isPending={isPending}>
          Save
        </Button>
      </Box>
    </Drawer>
  );
}
